import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault, loadable } from 'jotai/utils';
import { IListDataModel } from '../../data-models/list.data-model';
import { ListItemsResponse } from '../../data-models/list-item.data-model';
import { ITrackingDataModel } from '../../data-models/tracking.data-model';
import { fetchLists } from '../queries/MaggieListQueries';
import { fetchTrackingData } from '../queries/MaggieTrackingQueries';

type ListsValue = IListDataModel[] | null | Promise<IListDataModel[] | null>;
type TrackingValue = ITrackingDataModel[] | null | Promise<ITrackingDataModel[] | null>;

/**
 * Default fetches all CompanyProfiles lists for the current user on first
 * subscribe. Consumers using `useAtomValue` will suspend on first read; use
 * {@link listsStateLoadableJ} for non-suspending callers.
 */
export const listsStateJ = atomWithDefault<ListsValue>(async () => {
  return (await fetchLists()) ?? null;
});

export const listsStateLoadableJ = loadable(listsStateJ);

/**
 * Sync map of list id → list, derived from {@link listsStateJ}.
 *
 * Reads the async parent and is therefore itself async; consumers should
 * either suspend or wrap the read in `loadable`/`unwrap` if synchronous
 * access is required.
 */
export const listsByIdStateJ = atom(async (get): Promise<Map<number, IListDataModel>> => {
  const lists = await get(listsStateJ);
  const map = new Map<number, IListDataModel>();
  if (!lists) {
    return map;
  }
  for (const list of lists) {
    map.set(list.id, list);
  }
  return map;
});

/**
 * Default is `null`; the value is written imperatively by consumers (e.g.
 * `usePrepItemsData.saveCompanyData`, `useListActions.*`).
 *
 * `null` is also a valid key, used by call sites that pass
 * `activeCompanyListIdState` (which itself defaults to `null`).
 */
export const companyListStateJ = atomFamily((_listId: number | null) =>
  atom<Omit<ListItemsResponse, 'companies'> | null>(null)
);

export const activeCompanyListIdStateJ = atom<number | null>(null);

/**
 * Sync derived atom returning the currently selected list, or `null`.
 *
 * Reads the async {@link listsByIdStateJ}, so consumers will suspend until
 * the lists have loaded.
 */
export const activeListStateJ = atom(async (get): Promise<IListDataModel | null> => {
  const activeListId = get(activeCompanyListIdStateJ);
  if (activeListId == null) {
    return null;
  }
  const listsById = await get(listsByIdStateJ);
  return listsById.get(activeListId) ?? null;
});

export const togglePreviousDealsStateJ = atom<boolean>(false);

/**
 * Default fetches all tracking data for the current user on first subscribe.
 * Consumers always observe the resolved array (or `null` on failure).
 */
export const trackingDataStateJ = atomWithDefault<TrackingValue>(async () => {
  try {
    return await fetchTrackingData();
  } catch (err) {
    console.error(err);
    return null;
  }
});

/**
 * `loadable`-wrapped variant for non-suspending peeks.
 */
export const trackingDataStateLoadableJ = loadable(trackingDataStateJ);

/**
 * Async map of company id → tracking entry, derived from
 * {@link trackingDataStateJ}.
 */
export const trackingByCompanyIdStateJ = atom(async (get): Promise<Map<number, ITrackingDataModel>> => {
  const trackingData = await get(trackingDataStateJ);
  const map = new Map<number, ITrackingDataModel>();
  if (!trackingData) {
    return map;
  }
  for (const tracking of trackingData) {
    map.set(tracking.companyId, tracking);
  }
  return map;
});
